import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    postId: Number,
    url: String,
    tracked: { type: Boolean, default: false }
  }
  
  connect() {
    console.log('📊 Post View Tracker connected for post', this.postIdValue)
    this.setupIntersectionObserver()
  }
  
  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
  
  setupIntersectionObserver() {
    // Don't track if already tracked
    if (this.trackedValue) {
      return
    }
    
    const options = {
      root: null, // Use viewport as root
      rootMargin: '0px',
      threshold: 0.5 // Trigger when 50% of the post is visible
    }
    
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), options)
    this.observer.observe(this.element)
  }
  
  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.trackedValue) {
        console.log('👀 Post', this.postIdValue, 'entered viewport, tracking view')
        this.trackView()
      }
    })
  }
  
  async trackView() {
    // Mark as tracked immediately to prevent duplicate calls
    this.trackedValue = true
    
    try {
      const response = await fetch(`/posts/${this.postIdValue}/track_view`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (!response.ok) {
        this.disconnectObserver()
        return
      }

      const data = await response.json().catch(() => ({}))

      // Update the view count in the UI if element exists
      const viewCountElement = document.querySelector(`#post-stats-${this.postIdValue} [data-view-count]`)
      if (viewCountElement && data.views_count !== undefined) {
        viewCountElement.textContent = data.views_count
      }

      // Disconnect observer since we only want to track once per page load
      this.disconnectObserver()
    } catch {
      // View tracking is best-effort analytics; network failures must not
      // surface as app console errors or user-facing breakage.
      this.disconnectObserver()
    }
  }
  
  navigateToPost(event) {
    // Don't navigate if clicking on a link, button, or interactive element
    const target = event.target.closest('a, button, [data-action], input, textarea, select, [role="button"]')
    if (target && target !== this.element) return

    if (this.urlValue) {
      window.location.href = this.urlValue
    }
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  disconnectObserver() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
